import type {
  BillingItemType,
  ParsedBillingItem,
  ParsedCentPrecisionMoney,
} from 'ui/types/cartTypes';
import clsx from 'clsx';
import { getFormatters } from 'ui/hooks/formatter';
import { useShallow } from 'zustand/react/shallow';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useMemo } from 'react';
import { useTranslation } from '@whoop/i18n/lang/client';

interface BillingDetailsProps {
  billingDetails: ParsedBillingItem[];
}

interface StylizedBillingItem {
  title: string;
  formattedValue: string;
  containerStyle: string;
  titleStyle: string;
  valueStyle: string;
}

const sortOrder: Record<BillingItemType, number> = {
  SUBTOTAL: 0,
  DISCOUNT: 1,
  PENDING_AMOUNTS: 2,
  SHIPPING: 3,
  TAX: 4,
  TOTAL: 5,
  INCLUDED_SAVINGS: 6,
  INCLUDED_TAX: 7,
};

export default function BillingDetailsV2({
  billingDetails,
}: BillingDetailsProps): JSX.Element {
  const { language, region, currency } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      region: state.region,
      language: state.language,
    })),
  );
  const { t } = useTranslation('cart');
  const { formatPrice } = getFormatters({ currency, language, region });

  const getContainerStyle = (type: BillingItemType) =>
    clsx(
      'flex w-full flex-row justify-between',
      type === 'INCLUDED_TAX' ? 'pt-3' : '',
      type === 'INCLUDED_SAVINGS'
        ? 'bg-gray-50 mt-3 p-3 px-4 rounded-xl items-center'
        : 'mt-1 items-start',
    );

  const getTitleStyle = (type: BillingItemType) =>
    clsx(
      type === 'TOTAL' ? 'font-semibold' : '',
      type === 'INCLUDED_TAX' ? 'text-inactive' : '',
      'text-left text-base tracking-normal',
    );

  const getPriceStyle = (
    value: ParsedCentPrecisionMoney | null,
    type: BillingItemType,
  ) =>
    clsx(
      !value || type === 'INCLUDED_TAX' ? 'text-inactive' : '',
      type === 'TOTAL' ? 'font-semibold' : '',
      'text-right tracking-normal',
    );

  const getStylizedItem = (item: ParsedBillingItem): StylizedBillingItem => {
    const type = item.type;
    let formattedValue: string;
    if (item.value) {
      formattedValue = formatPrice(item.value?.centAmount ?? 0, {
        showCents: true,
      });
    } else {
      formattedValue = t('calculatedAtCheckout');
    }

    return {
      title: item.title,
      formattedValue,
      containerStyle: getContainerStyle(type),
      titleStyle: getTitleStyle(type),
      valueStyle: getPriceStyle(item.value, type),
    };
  };

  const shouldItemBeRemoved = (item: ParsedBillingItem): boolean => {
    return !item.value && item.type !== 'PENDING_AMOUNTS';
  };

  const getPendingItem = (
    items: ParsedBillingItem[],
  ): ParsedBillingItem | undefined => {
    const pendingItems = items.filter((item) => shouldItemBeRemoved(item));
    if (!pendingItems.length) return;
    let translationKey = 'shipping';
    if (region === 'IN') {
      translationKey = 'shippingAndDuties';
    } else if (pendingItems.some((item) => item.type === 'TAX')) {
      translationKey = 'shippingAndTax';
    }
    return {
      title: t(translationKey),
      value: null,
      type: 'PENDING_AMOUNTS',
    };
  };

  // Note: This reformatting can be removed once the backend is supplying the totals in this format already:
  // - One 'PENDING_AMOUNTS' item pre-checkout representing totals that can only be calculated after shipping address is set, rather than individual "Shipping ...", "Tax ...", etc. items
  // - Cart discounts represented as an 'INCLUDED_SAVINGS' item rather than a negative 'DISCOUNT' item
  // - Subtotal amount reflecting 'INCLUDED_SAVINGS' removed, rather than doing the math on the frontend
  const reformatItems = (items: ParsedBillingItem[]) => {
    // Only show 1 "calculated at checkout" item
    const pendingItem = getPendingItem(items);
    const discount = items.find((item) => item.type === 'DISCOUNT');
    let pendingItemAdded = false;
    return items
      .reduce((result: ParsedBillingItem[], item) => {
        const shouldRemove = shouldItemBeRemoved(item);
        if (shouldRemove && pendingItem && !pendingItemAdded) {
          result.push(pendingItem);
          pendingItemAdded = true;
        } else if (item.type === 'SUBTOTAL' && item.value && discount) {
          const newValue = {
            ...item.value,
            centAmount:
              item.value.centAmount - (discount.value?.centAmount ?? 0),
          };
          result.push({ ...item, value: newValue });
        } else if (item.type === 'DISCOUNT') {
          result.push({
            ...item,
            title: t('youreSaving'),
            type: 'INCLUDED_SAVINGS',
          });
        } else if (!shouldRemove) {
          result.push(item);
        }
        return result;
      }, [])
      .sort((a, b) => sortOrder[a.type] - sortOrder[b.type]);
  };

  const stylizedItems: StylizedBillingItem[] = useMemo(() => {
    return reformatItems(billingDetails).map((item) => getStylizedItem(item));
  }, [billingDetails]);

  return (
    <div className='flex w-full flex-col items-center justify-center'>
      {stylizedItems.map((item) => (
        <div className={item.containerStyle} key={item.title}>
          <p className={item.titleStyle}>{item.title}</p>
          <p className={item.valueStyle}>{item.formattedValue}</p>
        </div>
      ))}
    </div>
  );
}
